import React, { useState } from "react";
import { FC } from "react";
import { CSSObject, IconButton, Box, Dialog, Typography } from "@mui/material";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import PinterestIcon from "@mui/icons-material/Pinterest";

const TikTokIcon = ({ color = "#000000" }) => {
  return (
    <svg
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 50 50"
      width="24px"
      height="24px"
    >
      <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z" />
    </svg>
  );
};

import Link from "@mui/material/Link";

import IATANLogo from "../../assets/images/IATANLogo.webp";
import BBBLogo from "../../assets/images/BBBLogo.png";

import LogoImg from "../../assets/images/logo-new.png";
import Pup from "../../assets/images/pup.png";
import "./Footer.scss";

interface Props {
  sx?: CSSObject;
}

const Footer: FC<Props> = ({ sx }) => {
  const [showEgg, setShowEgg] = useState(false);
  const [timer, setTimer] = useState(null);

  const easterEgg = () => {
    if (timer) {
      clearTimeout(timer);
    }
    const newTimer = setTimeout(() => {
      setShowEgg(true);
    }, 4000);
    setTimer(newTimer);
  };

  const handleClose = () => {
    setShowEgg(false);
  };

  const SiteLinkSection = ({ header, links }) => (
    <Box>
      <Typography variant="h6">{header}</Typography>
      <Box display="flex" flexDirection="column">
        {links.map((link) => {
          if (link.isBlank) {
            return (
              <Link
                my="0.5rem"
                key={link.href}
                href={link.href}
                target="_blank"
              >
                {link.text}
              </Link>
            );
          }
          return (
            <Link my="0.5rem" key={link.href} href={link.href}>
              {link.text}
            </Link>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <>
      {showEgg && (
        <Dialog onClose={handleClose} open={showEgg}>
          <img loading="lazy" alt="pup" src={Pup} />
        </Dialog>
      )}
      <Box mt="4rem" mb="1.5rem">
        <Box
          sx={{
            p: "2rem",
            gap: "2rem",
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" },
            justifyContent: "space-around",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Link href="/">
              <img
                loading="lazy"
                width="220px"
                src={LogoImg}
                alt="Romingo Logo"
              />
            </Link>
            <Typography
              component="p"
              variant="base"
              maxWidth="400px"
              mt="0.5rem"
              mb="0.5rem"
            >
              Romingo is the easiest way to book pet-friendly travel.
              Hand-selected hotels, responsive customer service, and the lowest
              rates guaranteed provide a truly pet-friendly experience. Roam the
              world freely with Romingo.
            </Typography>
            <Typography
              component="p"
              variant="base"
              maxWidth="400px"
              mt="0.75rem"
              mb="0.75rem"
            >
              Traveling with your pet? Be sure to <b>#romingotravel</b>
            </Typography>
            <div className="social-icons">
              <IconButton
                edge="start"
                sx={{ mr: 1, color: "black" }}
                aria-label="menu"
                href="https://www.facebook.com/RomingoTravel/"
                target="_blank"
              >
                <FacebookRoundedIcon />
              </IconButton>
              <IconButton
                edge="start"
                sx={{ mr: 1, color: "black" }}
                aria-label="menu"
                href="https://www.instagram.com/romingotravel/"
                target="_blank"
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                edge="start"
                sx={{ mr: 1, color: "black" }}
                aria-label="menu"
                href="https://www.tiktok.com/@romingotravel"
                target="_blank"
              >
                <TikTokIcon />
              </IconButton>
              <IconButton
                edge="start"
                sx={{ mr: 1, color: "black" }}
                aria-label="menu"
                href="https://in.pinterest.com/romingotravel/"
                target="_blank"
              >
                <PinterestIcon />
              </IconButton>
            </div>
          </Box>
          <SiteLinkSection
            header="Site map"
            links={[
              { href: "/about", text: "About us" },
              { href: "/faq", text: "FAQ" },
              { href: "/list-your-property", text: "List your property" },
              { href: "/contact", text: "Contact" },
              { href: "/romingo-partners", text: "Community" },
            ]}
          />
          <SiteLinkSection
            header="Content & Blog"
            links={[
              {
                href: "https://storage.googleapis.com/romingo-development-public/ebooks/Air%20Travel%20Guide.pdf",
                text: "Guide to Air Travel With Your Dog",
              },
              { href: "/blog", text: "Romingo Blog", isBlank: true },
              { href: "/blog?tag=12", text: "Top travel tips", isBlank: true },
            ]}
          />

          <Box
            sx={{
              marginTop: 0,
              paddingTop: 0,
              marginLeft: "1rem",
              display: "flex",
              flexDirection: { xs: "row", sm: "row", md: "row", lg: "column" },
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <img
              width="140px"
              src={IATANLogo}
              alt="IATAN Logo"
              loading="lazy"
            />
            <img
              style={{ marginLeft: "-1rem" }}
              width="160px"
              src={BBBLogo}
              alt="BBB Logo"
              loading="lazy"
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          m: "1rem",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <div
          onMouseLeave={() => clearTimeout(timer)}
          onMouseEnter={() => easterEgg()}
          className="footer-wrapper-year"
        >
          © 2025 Romingo, Inc.
        </div>
        <Link style={{ marginLeft: "1em" }} href="/privacy">
          Privacy policy
        </Link>
        <Link style={{ marginLeft: "1em" }} href="/terms-of-use">
          Terms & Conditions
        </Link>
        <Link style={{ marginLeft: "1em" }} href="/romingo-rewards">
          Romingo Rewards
        </Link>
        <Link style={{ marginLeft: "1em" }} href="/sitemap.xml">
          Sitemap
        </Link>
      </Box>
    </>
  );
};

export default Footer;
